'use client';

import React, { useCallback, useState } from 'react';
import { CitationMap } from '@/lib/utils/citations';
import {
  CitationPill,
  getSourceDisplayTitle,
  getSourceIcon,
  getTypeLabel,
  getPageSourceLabel,
} from '@/components/features/chat/components/ui/CitationPill';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { useCitationContext } from '@/lib/contexts/CitationContext';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CitationGroupProps {
  /** Array of 1-based citation numbers in this group (adjacent [1][2][3]) */
  citationNumbers: number[];
  /** The citation map for resolving source data */
  citationMap: CitationMap;
}

/**
 * Groups adjacent inline citations Perplexity-style:
 * - Single citation: renders a plain CitationPill (click scrolls directly).
 * - Multiple citations: renders a trigger pill (first source title + "+N").
 *   Clicking the trigger opens a Radix Popover listing all sources.
 *   Clicking any list item scrolls to and highlights that source in the panel.
 */
export const CitationGroup: React.FC<CitationGroupProps> = ({
  citationNumbers,
  citationMap,
}) => {
  const ctx = useCitationContext();

  // Filter to only valid citation numbers that exist in the map
  const validNumbers = citationNumbers.filter((n) => citationMap[n]);

  const [open, setOpen] = useState(false);

  const handleItemClick = useCallback(
    (num: number) => {
      setOpen(false);
      // Small delay so the popover closes before scrolling
      setTimeout(() => {
        ctx?.scrollToSource(num);
      }, 50);
    },
    [ctx]
  );

  if (validNumbers.length === 0) return null;

  // Single citation — no popup needed, click directly scrolls
  if (validNumbers.length === 1) {
    return (
      <CitationPill
        citationNumber={validNumbers[0]}
        citationMap={citationMap}
      />
    );
  }

  // Multiple citations — trigger pill + popup list
  const firstNum = validNumbers[0];
  const firstSource = citationMap[firstNum];
  const FirstIcon = getSourceIcon(firstSource.type);
  const firstTitle = getSourceDisplayTitle(firstSource);
  const remaining = validNumbers.length - 1;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Trigger pill: first source icon+title + "+N" badge */}
        <button
          type="button"
          aria-label={`${validNumbers.length} citations — click to view all`}
          className="citation-pill inline-flex items-center gap-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[9px] font-medium h-[16px] px-1 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 active:bg-purple-300 align-sub mx-[1px] transition-colors leading-none select-none max-w-[150px] sm:max-w-[200px]"
        >
          <FirstIcon className="h-2.5 w-2.5 flex-shrink-0" />
          <span className="truncate">{firstTitle}</span>
          <span className="ml-0.5 flex-shrink-0 rounded bg-purple-300 dark:bg-purple-700 px-0.5 font-semibold">
            +{remaining}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        className="w-72 p-1.5"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Source list */}
        <div className="flex flex-col gap-0.5">
          {validNumbers.map((num) => {
            const source = citationMap[num];
            if (!source) return null;
            const Icon = getSourceIcon(source.type);
            const title = getSourceDisplayTitle(source);
            const typeLabel = getTypeLabel(source.type);

            return (
              <button
                key={num}
                type="button"
                onClick={() => handleItemClick(num)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors w-full"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="flex flex-col min-w-0 text-left">
                  <span className="font-medium text-gray-800 dark:text-gray-100 leading-tight line-clamp-2 text-xs">
                    {title}
                  </span>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {[getPageSourceLabel(source), typeLabel].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

CitationGroup.displayName = 'CitationGroup';

